import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useJobsRealtime, JOB_STATUS, needsReview } from '../hooks/useJobsRealtime';
import { usePortfolioContext } from './PortfolioContext';
import { useAuthContext } from './AuthContext';
import SwitchPortfolioModal from '../components/SwitchPortfolioModal';
import { IMPORTS_RESULT_ROUTE_PREFIX, IMPORTS_REVIEW_ROUTE_PREFIX, IMPORTS_ROUTE, STOCK_IMPORTS_RESULT_ROUTE_PREFIX, STOCK_IMPORTS_REVIEW_ROUTE_PREFIX } from '../constants/routes';

const JobNotificationsContext = createContext(null);

// Notification types
const NOTIFICATION_TYPE = {
  IMPORT_REVIEW: 'import_review',
  IMPORT_DONE: 'import_done',
  IMPORT_FAILED: 'import_failed',
  EXPORT_DONE: 'export_done',
  EXPORT_FAILED: 'export_failed',
};

const parseJobInput = (input) => {
  if (!input) return null;
  if (typeof input === 'object') return input;
  try {
    return JSON.parse(input);
  } catch (_err) {
    return null;
  }
};

const isStockImportJob = (job) => parseJobInput(job?.input)?.asset_kind === 'stock';

export const JobNotificationsProvider = ({ children }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthContext();
  const { portfolios, currentPortfolioId, selectPortfolio } = usePortfolioContext();
  
  // Track notifications per portfolio: { [portfolioId]: [...notifications] }
  const [notificationsByPortfolio, setNotificationsByPortfolio] = useState({});
  const [lastSeenAtByPortfolio, setLastSeenAtByPortfolio] = useState({});
  const [hydrated, setHydrated] = useState(false);
  
  // Track which job IDs we've already notified about to prevent duplicates
  const [notifiedJobIds, setNotifiedJobIds] = useState(new Set());

  // Switch portfolio modal state
  const [switchModalState, setSwitchModalState] = useState({
    isOpen: false,
    portfolioId: null,
    portfolioName: null,
    targetPath: null,
  });

  // Extract portfolio IDs for realtime subscription
  const portfolioIds = useMemo(
    () => portfolios?.map((p) => p.id) || [],
    [portfolios]
  );

  // Load persisted state from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem('jobNotifications');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') setNotificationsByPortfolio(parsed);
      }
      const seenMap = localStorage.getItem('jobNotificationsSeenAtByPortfolio');
      if (seenMap) {
        const parsedSeen = JSON.parse(seenMap);
        if (parsedSeen && typeof parsedSeen === 'object') setLastSeenAtByPortfolio(parsedSeen);
      }
      // Load notified job IDs to prevent re-notifying on refresh
      const notifiedRaw = localStorage.getItem('jobNotificationsNotifiedIds');
      if (notifiedRaw) {
        const ids = JSON.parse(notifiedRaw);
        if (Array.isArray(ids)) setNotifiedJobIds(new Set(ids));
      }
    } catch {}
    setHydrated(true);
  }, []);

  // Persist notifications to localStorage
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem('jobNotifications', JSON.stringify(notificationsByPortfolio));
    } catch {}
  }, [notificationsByPortfolio, hydrated]);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem('jobNotificationsSeenAtByPortfolio', JSON.stringify(lastSeenAtByPortfolio));
    } catch {}
  }, [lastSeenAtByPortfolio, hydrated]);

  useEffect(() => {
    if (!hydrated) return;
    try {
      // Only keep last 100 notified IDs to prevent unbounded growth
      const idsArray = Array.from(notifiedJobIds).slice(-100);
      localStorage.setItem('jobNotificationsNotifiedIds', JSON.stringify(idsArray));
    } catch {}
  }, [notifiedJobIds, hydrated]);

  // Navigate to job page, handling portfolio switch if needed
  const navigateToJob = useCallback((jobId, portfolioId, path) => {
    if (portfolioId === currentPortfolioId) {
      // Same portfolio - navigate directly
      navigate(path);
    } else {
      // Different portfolio - show switch modal
      const portfolio = portfolios.find((p) => p.id === portfolioId);
      setSwitchModalState({
        isOpen: true,
        portfolioId,
        portfolioName: portfolio?.name || 'another portfolio',
        targetPath: path,
      });
    }
  }, [currentPortfolioId, portfolios, navigate]);

  // Handle switch portfolio modal confirm
  const handleSwitchConfirm = useCallback(() => {
    const { portfolioId, targetPath } = switchModalState;
    const portfolio = portfolios.find((p) => p.id === portfolioId);
    if (portfolio) {
      selectPortfolio(portfolio);
    }
    setSwitchModalState({ isOpen: false, portfolioId: null, portfolioName: null, targetPath: null });
    if (targetPath) {
      // Small delay to let portfolio context update
      setTimeout(() => navigate(targetPath), 100);
    }
  }, [switchModalState, portfolios, selectPortfolio, navigate]);

  // Handle switch portfolio modal close
  const handleSwitchClose = useCallback(() => {
    setSwitchModalState({ isOpen: false, portfolioId: null, portfolioName: null, targetPath: null });
  }, []);

  // Handle job updates from Supabase Realtime
  const handleJobUpdate = useCallback((job) => {
    const { id: jobId, portfolio_id: portfolioId, status, stage, type, error } = job;
    
    // Only handle transaction import and export jobs
    const isImport = type === 'transactions_import';
    const isExport = type === 'transactions_export';
    const isStockImport = isImport && isStockImportJob(job);
    const reviewRoutePrefix = isStockImport ? STOCK_IMPORTS_REVIEW_ROUTE_PREFIX : IMPORTS_REVIEW_ROUTE_PREFIX;
    const resultRoutePrefix = isStockImport ? STOCK_IMPORTS_RESULT_ROUTE_PREFIX : IMPORTS_RESULT_ROUTE_PREFIX;
    if (!isImport && !isExport) return;
    
    // Check if we've already notified about this job in this state
    const notificationKey = `${jobId}-${status}-${stage || ''}`;
    if (notifiedJobIds.has(notificationKey)) return;

    let notification = null;
    let toastMessage = null;
    let toastType = 'success';
    let targetPath = null;

    if (isImport) {
      // Job needs review (waiting + REVIEWING stage)
      if (needsReview(job)) {
        notification = {
          id: jobId,
          portfolioId,
          type: NOTIFICATION_TYPE.IMPORT_REVIEW,
          status: 'review',
          message: 'Import ready for review',
          createdAt: new Date().toISOString(),
          jobId,
        };
        toastMessage = 'Import job ready for review';
        toastType = 'info';
        targetPath = `${reviewRoutePrefix}/${jobId}`;
      }
      // Job succeeded
      else if (status === JOB_STATUS.SUCCEEDED) {
        notification = {
          id: jobId,
          portfolioId,
          type: NOTIFICATION_TYPE.IMPORT_DONE,
          status: 'succeeded',
          message: 'Import completed successfully',
          createdAt: new Date().toISOString(),
          jobId,
        };
        toastMessage = 'Import completed successfully';
        toastType = 'success';
        targetPath = `${resultRoutePrefix}/${jobId}`;
      }
      // Job failed
      else if (status === JOB_STATUS.FAILED || status === JOB_STATUS.TIMEOUT) {
        notification = {
          id: jobId,
          portfolioId,
          type: NOTIFICATION_TYPE.IMPORT_FAILED,
          status: 'failed',
          message: error || 'Import failed',
          createdAt: new Date().toISOString(),
          jobId,
        };
        toastMessage = error ? `Import failed: ${error}` : 'Import failed';
        toastType = 'error';
        targetPath = `${resultRoutePrefix}/${jobId}`;
      }
    } else if (isExport) {
      // Export succeeded
      if (status === JOB_STATUS.SUCCEEDED) {
        notification = {
          id: jobId,
          portfolioId,
          type: NOTIFICATION_TYPE.EXPORT_DONE,
          status: 'succeeded',
          message: 'Export completed successfully',
          createdAt: new Date().toISOString(),
          jobId,
        };
        toastMessage = 'Export completed! Click to download.';
        toastType = 'success';
        targetPath = IMPORTS_ROUTE;
      }
      // Export failed
      else if (status === JOB_STATUS.FAILED || status === JOB_STATUS.TIMEOUT) {
        notification = {
          id: jobId,
          portfolioId,
          type: NOTIFICATION_TYPE.EXPORT_FAILED,
          status: 'failed',
          message: error || 'Export failed',
          createdAt: new Date().toISOString(),
          jobId,
        };
        toastMessage = error ? `Export failed: ${error}` : 'Export failed';
        toastType = 'error';
        targetPath = IMPORTS_ROUTE;
      }
    }

    if (notification) {
      const notificationWithTargetPath = targetPath ? { ...notification, targetPath } : notification;
      // Add notification to state
      setNotificationsByPortfolio((prev) => {
        const list = prev[portfolioId] || [];
        // Avoid duplicates
        if (list.some((n) => n.id === jobId && n.type === notificationWithTargetPath.type)) {
          return prev;
        }
        return { ...prev, [portfolioId]: [notificationWithTargetPath, ...list].slice(0, 50) };
      });

      // Mark as notified
      setNotifiedJobIds((prev) => new Set([...prev, notificationKey]));

      // Show clickable toast
      if (toastMessage && targetPath) {
        const toastOptions = {
          duration: toastType === 'error' ? 6000 : 4000,
          style: { cursor: 'pointer' },
        };

        const handleToastClick = () => {
          toast.dismiss();
          navigateToJob(jobId, portfolioId, targetPath);
        };

        if (toastType === 'error') {
          toast.error(
            (t) => (
              <span onClick={handleToastClick}>
                {toastMessage}
                <span className="block text-xs opacity-75 mt-1">Click to view details</span>
              </span>
            ),
            toastOptions
          );
        } else if (toastType === 'info') {
          toast(
            (t) => (
              <span onClick={handleToastClick}>
                {toastMessage}
                <span className="block text-xs opacity-75 mt-1">Click to review</span>
              </span>
            ),
            { ...toastOptions, icon: '📝' }
          );
        } else {
          toast.success(
            (t) => (
              <span onClick={handleToastClick}>
                {toastMessage}
                <span className="block text-xs opacity-75 mt-1">Click to view results</span>
              </span>
            ),
            toastOptions
          );
        }
      }
    }
  }, [notifiedJobIds, navigateToJob]);

  // Subscribe to realtime job updates
  useJobsRealtime({
    portfolioIds,
    onJobUpdate: handleJobUpdate,
    enabled: isAuthenticated && portfolioIds.length > 0,
  });

  // Remove a specific notification
  const removeNotification = useCallback((portfolioId, jobId) => {
    setNotificationsByPortfolio((prev) => {
      const list = prev[portfolioId] || [];
      return { ...prev, [portfolioId]: list.filter((n) => n.id !== jobId) };
    });
  }, []);

  // Clear all notifications for a portfolio
  const clearAll = useCallback((portfolioId) => {
    setNotificationsByPortfolio((prev) => ({ ...prev, [portfolioId]: [] }));
  }, []);

  // Mark all as seen (across all portfolios)
  const markAllSeen = useCallback(() => {
    const now = new Date().toISOString();
    setLastSeenAtByPortfolio((prev) => {
      const next = { ...prev };
      Object.keys(notificationsByPortfolio).forEach((pf) => {
        next[pf] = now;
      });
      return next;
    });
  }, [notificationsByPortfolio]);

  // Mark specific portfolio as seen
  const markPortfolioSeen = useCallback((portfolioId) => {
    if (!portfolioId) return;
    const now = new Date().toISOString();
    setLastSeenAtByPortfolio((prev) => ({ ...prev, [portfolioId]: now }));
  }, []);

  // Get unread count for a portfolio
  const getUnreadCount = useCallback((portfolioId) => {
    if (!portfolioId) return 0;
    const list = notificationsByPortfolio[portfolioId] || [];
    const seen = lastSeenAtByPortfolio[portfolioId];
    if (!seen) return list.length;
    const seenTime = new Date(seen).getTime();
    return list.filter((n) => new Date(n.createdAt).getTime() > seenTime).length;
  }, [notificationsByPortfolio, lastSeenAtByPortfolio]);

  // Total unread count across all portfolios
  const unreadCount = useMemo(() => {
    return Object.keys(notificationsByPortfolio).reduce(
      (acc, pf) => acc + getUnreadCount(pf),
      0
    );
  }, [notificationsByPortfolio, getUnreadCount]);

  // Get notifications for a specific portfolio
  const getNotifications = useCallback((portfolioId) => {
    return notificationsByPortfolio[portfolioId] || [];
  }, [notificationsByPortfolio]);

  const value = useMemo(
    () => ({
      notificationsByPortfolio,
      getNotifications,
      removeNotification,
      clearAll,
      markAllSeen,
      markPortfolioSeen,
      getUnreadCount,
      unreadCount,
      navigateToJob,
    }),
    [
      notificationsByPortfolio,
      getNotifications,
      removeNotification,
      clearAll,
      markAllSeen,
      markPortfolioSeen,
      getUnreadCount,
      unreadCount,
      navigateToJob,
    ]
  );

  return (
    <JobNotificationsContext.Provider value={value}>
      {children}
      <SwitchPortfolioModal
        isOpen={switchModalState.isOpen}
        onClose={handleSwitchClose}
        onConfirm={handleSwitchConfirm}
        portfolioName={switchModalState.portfolioName}
        targetPath={switchModalState.targetPath}
      />
    </JobNotificationsContext.Provider>
  );
};

export const useJobNotifications = () => {
  const ctx = useContext(JobNotificationsContext);
  if (!ctx) throw new Error('useJobNotifications must be used within JobNotificationsProvider');
  return ctx;
};
